// src/pages/AdminProdutosList.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { db, storage } from '../firebase';
import { collection, getDocs, doc, addDoc, updateDoc, deleteDoc, query } from 'firebase/firestore';
// 'listAll' removido da linha abaixo
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

// Importando o editor e seus estilos
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import EditorToolbar from '../components/EditorToolbar';
import '../pages/PostEditor.css'; // Reutilizando estilos do editor de post

import '../components/AdminModals.css';

const AdminProdutosList = () => {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    // --- Estados para o Modal
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentProduct, setCurrentProduct] = useState(null); // Guarda o produto sendo editado
    const [formData, setFormData] = useState({});
    const [isUploading, setIsUploading] = useState(false);
    const mainImageRef = useRef(null);

    // Configuração do Editor "Rich Text" para o campo 'description'
    const editor = useEditor({
        extensions: [StarterKit],
        content: '',
        onUpdate: ({ editor }) => {
            setFormData(prev => ({ ...prev, description: editor.getHTML() }));
        },
    });

    // Função para buscar todos os dados necessários
    const fetchData = useCallback(async () => {
        try {
            const catPath = "pts-com-br/fabricantes/novotechnik_categories";
            const prodPath = "pts-com-br/fabricantes/novotechnik_products";

            const catSnapshot = await getDocs(collection(db, catPath));
            setCategories(catSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

            const prodSnapshot = await getDocs(query(collection(db, prodPath)));
            setProducts(prodSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        } catch (err) {
            console.error("Erro ao buscar dados:", err);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Atualiza o conteúdo do editor quando abrimos o modal para editar um produto
    useEffect(() => {
        if (editor && isModalOpen) {
            editor.commands.setContent(formData.description || '');
        }
    }, [editor, isModalOpen, formData.description]);
    
    // NOTE: A funcionalidade de Media Modal foi removida temporariamente para corrigir o build.
    // As variáveis 'isMediaModalOpen', 'storageImages', 'loadingMedia' e as funções
    // 'fetchStorageImages', 'handleOpenMediaModal', 'handleSelectExistingImage' foram removidas.

    // Funções para controlar o Modal
    const handleOpenModal = (product = null) => {
        setCurrentProduct(product);
        if (product) {
            setFormData(product); // Carrega todos os dados do produto no formulário
        } else {
            // Define o estado inicial para um novo produto
            setFormData({
                name: '', sku: '', categoryId: '', shortDescription: '',
                description: '', mainImage: '', imageGallery: [], tags: [], technicalSpecs: {}
            });
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setCurrentProduct(null);
    };

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setIsUploading(true);
        const storageRef = ref(storage, `novotechnik/products/${file.name}`);
        try {
            await uploadBytes(storageRef, file);
            const downloadURL = await getDownloadURL(storageRef);
            setFormData(prev => ({ ...prev, mainImage: downloadURL }));
        } catch (error) {
            console.error("Erro no upload da imagem:", error);
        } finally {
            setIsUploading(false);
        }
    };
    
    // Salvar (Criar ou Atualizar)
    const handleSaveProduct = async (e) => {
        e.preventDefault();
        const productData = { ...formData };
        
        try {
            const prodPath = "pts-com-br/fabricantes/novotechnik_products";
            if (currentProduct) {
                // Atualizar
                const docRef = doc(db, prodPath, currentProduct.id);
                await updateDoc(docRef, productData);
            } else {
                // Criar
                await addDoc(collection(db, prodPath), productData);
            }
            fetchData();
            handleCloseModal();
        } catch (err) {
            console.error("Erro ao salvar produto:", err);
            alert("Ocorreu um erro ao salvar o produto.");
        }
    };

    // Apagar
    const handleDeleteProduct = async (productId, productName) => {
        if (window.confirm(`Tem certeza que deseja apagar o produto "${productName}"?`)) {
            try {
                const docRef = doc(db, "pts-com-br/fabricantes/novotechnik_products", productId);
                await deleteDoc(docRef);
                fetchData();
            } catch (err) {
                console.error("Erro ao apagar produto:", err);
            }
        }
    };

    return (
        <div className="admin-list-container">
            <div className="admin-list-header">
                <h1>Gerenciamento de Produtos</h1>
                <button onClick={() => handleOpenModal()} className="create-button">+ Novo Produto</button>
            </div>

            <div className="table-wrapper">
                <table>
                    <thead>
                        <tr>
                            <th>Produto</th>
                            <th>SKU</th>
                            <th>Categoria</th>
                            <th>Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {products.map(prod => (
                            <tr key={prod.id}>
                                <td>{prod.name}</td>
                                <td>{prod.sku}</td>
                                <td>{categories.find(c => c.id === prod.categoryId)?.name || 'Sem Categoria'}</td>
                                <td className="actions-cell">
                                    <button onClick={() => handleOpenModal(prod)} className="action-button edit-button">Editar</button>
                                    <button onClick={() => handleDeleteProduct(prod.id, prod.name)} className="action-button delete-button">Apagar</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {isModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content large">
                        <h2>{currentProduct ? 'Editar Produto' : 'Novo Produto'}</h2>
                        <form onSubmit={handleSaveProduct}>
                            <div className="form-grid-2-cols">
                                <div className="form-group">
                                    <label>Nome do Produto</label>
                                    <input type="text" name="name" value={formData.name || ''} onChange={handleFormChange} required />
                                </div>
                                <div className="form-group">
                                    <label>SKU (Código)</label>
                                    <input type="text" name="sku" value={formData.sku || ''} onChange={handleFormChange} required />
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Categoria</label>
                                <select name="categoryId" value={formData.categoryId || ''} onChange={handleFormChange} required>
                                    <option value="">Selecione uma categoria...</option>
                                    {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                                </select>
                            </div>

                            <div className="form-group">
                                <label>Imagem Principal</label>
                                <input type="file" ref={mainImageRef} onChange={handleImageUpload} style={{ display: 'none' }} accept="image/*" />
                                <div className="upload-options">
                                    <button type="button" className="upload-button" onClick={() => mainImageRef.current.click()} disabled={isUploading}>
                                        {isUploading ? 'Enviando...' : 'Carregar Nova'}
                                    </button>
                                    {/* O botão "Usar Existente" foi desativado temporariamente */}
                                </div>
                                {formData.mainImage && <img src={formData.mainImage} alt="Preview" className="image-preview" />}
                            </div>

                            <div className="form-group">
                                <label>Descrição Curta</label>
                                <textarea name="shortDescription" rows="3" value={formData.shortDescription || ''} onChange={handleFormChange}></textarea>
                            </div>

                            <div className="form-group">
                                <label>Descrição Completa (com formatação)</label>
                                <div className="editor-wrapper">
                                    <EditorToolbar editor={editor} />
                                    <EditorContent editor={editor} />
                                </div>
                            </div>

                            <div className="modal-actions">
                                <button type="button" onClick={handleCloseModal} className="button-cancel">Cancelar</button>
                                <button type="submit" className="button-save" disabled={isUploading}>
                                    {isUploading ? 'Aguarde o Upload' : 'Salvar Produto'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            
        </div>
    );
};

export default AdminProdutosList;